import { distanceUnit, FIELDS, metresPer, weightStep, type ExerciseType, type SetValues, type Units } from '@squats/shared';
import { useState, type FormEvent } from 'react';

export type SetExtras = { warmup: boolean };

/**
 * The numbers for one set, pre-filled, with big − / + buttons so a set is usually one tap on "Log set".
 * Durations are entered in seconds (holds) or minutes (cardio); distance in mi or km by the person's units.
 */
export function SetEntry({
  type,
  units,
  initial,
  initialExtras = { warmup: false },
  submitLabel,
  onSubmit,
  children,
}: {
  type: ExerciseType;
  units: Units;
  initial: SetValues;
  initialExtras?: SetExtras;
  submitLabel: string;
  onSubmit: (values: SetValues, extras: SetExtras) => void | Promise<void>;
  children?: React.ReactNode;
}) {
  const fields = FIELDS[type];
  const [weight, setWeight] = useState(numText(initial.weight));
  const [reps, setReps] = useState(numText(initial.reps));
  // Holds in seconds; cardio in minutes.
  const [duration, setDuration] = useState(numText(initial.durationS === null ? null : type === 'cardio' ? +(initial.durationS / 60).toFixed(2) : initial.durationS));
  const [distance, setDistance] = useState(numText(initial.distanceM === null ? null : +(initial.distanceM / metresPer(units)).toFixed(2)));
  const [warmup, setWarmup] = useState(initialExtras.warmup);
  const [busy, setBusy] = useState(false);

  const values: SetValues = {
    weight: fields.includes('weight') ? parse(weight) : null,
    reps: fields.includes('reps') ? int(reps) : null,
    durationS: fields.includes('durationS') ? toSeconds(duration, type) : null,
    distanceM: fields.includes('distanceM') ? toMetres(distance, units) : null,
  };
  // Something to record: reps for rep exercises, a time for holds and cardio.
  const valid =
    (type === 'weight_reps' || type === 'bodyweight_reps' ? (values.reps ?? 0) > 0 : (values.durationS ?? 0) > 0) &&
    (!fields.includes('weight') || weight.trim() === '' || values.weight !== null);

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    if (!valid || busy) return;
    setBusy(true);
    try {
      await onSubmit(values, { warmup });
    } finally {
      setBusy(false);
    }
  };

  return (
    <form className="set-entry stack" onSubmit={submit}>
      <div className="steppers">
        {fields.includes('weight') && <Stepper label={`Weight (${units})`} value={weight} onChange={setWeight} step={weightStep(units)} decimal placeholder="—" />}
        {fields.includes('reps') && <Stepper label="Reps" value={reps} onChange={setReps} step={1} />}
        {fields.includes('durationS') && <Stepper label={type === 'cardio' ? 'Minutes' : 'Seconds'} value={duration} onChange={setDuration} step={type === 'cardio' ? 1 : 5} decimal={type === 'cardio'} />}
        {fields.includes('distanceM') && <Stepper label={`Distance (${distanceUnit(units)})`} value={distance} onChange={setDistance} step={0.1} decimal placeholder="—" />}
      </div>
      <div className="row wrap set-extras">
        {type !== 'cardio' && (
          <button type="button" className={`chip toggle${warmup ? ' on' : ''}`} aria-pressed={warmup} onClick={() => setWarmup((w) => !w)}>
            Warm-up
          </button>
        )}
        <span className="grow" />
        {children}
        <button type="submit" className="btn primary" disabled={!valid || busy}>
          {submitLabel}
        </button>
      </div>
    </form>
  );
}

function Stepper({
  label,
  value,
  onChange,
  step,
  decimal = false,
  placeholder,
}: {
  label: string;
  value: string;
  onChange: (v: string) => void;
  step: number;
  decimal?: boolean;
  placeholder?: string;
}) {
  const nudge = (dir: 1 | -1) => {
    const n = parse(value) ?? 0;
    const next = Math.max(0, Math.round((n + dir * step) * 100) / 100);
    onChange(String(next));
  };
  return (
    <label className="stepper">
      <span className="muted small">{label}</span>
      <span className="stepper-row">
        <button type="button" className="btn" onClick={() => nudge(-1)} aria-label={`Less ${label}`}>
          −
        </button>
        <input
          inputMode={decimal ? 'decimal' : 'numeric'}
          value={value}
          placeholder={placeholder}
          onChange={(e) => onChange(e.target.value.replace(',', '.'))}
          onFocus={(e) => e.target.select()}
          aria-label={label}
        />
        <button type="button" className="btn" onClick={() => nudge(1)} aria-label={`More ${label}`}>
          +
        </button>
      </span>
    </label>
  );
}

const numText = (n: number | null) => (n === null ? '' : String(n));
function parse(s: string): number | null {
  if (s.trim() === '') return null;
  const n = Number(s);
  return Number.isFinite(n) && n >= 0 ? n : null;
}
const int = (s: string) => {
  const n = parse(s);
  return n === null ? null : Math.round(n);
};
const toSeconds = (s: string, type: ExerciseType) => {
  const n = parse(s);
  return n === null ? null : Math.round(type === 'cardio' ? n * 60 : n);
};
const toMetres = (s: string, units: Units) => {
  const n = parse(s);
  return n === null ? null : Math.round(n * metresPer(units));
};
